import { DepositWithdrawalRequestHistory } from '@/types/sso';
import { capitalize } from '@/utils/format';
import { getCurrency } from '@/utils/hooks/useCurrency';
import { UseLanguageReturn } from '@/utils/hooks/useLanguage';
import { PortalDepositRow } from '@/utils/hooks/usePortalDepositTable';
import { CellContext } from '@tanstack/react-table';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import getSymbolFromCurrency from 'currency-symbol-map';
import { MdErrorOutline, MdOutlineCheckCircleOutline, MdOutlineQueryBuilder } from 'react-icons/md';
import { generalAmountCell } from './currencyHelper';

interface PortalDepositColumnsParams {
    lang: UseLanguageReturn;
    formatQty: Intl.NumberFormat;
    formatDate: Intl.DateTimeFormat;
}

export const portalDepositColumns = (params: PortalDepositColumnsParams) => {
    const { lang, formatQty, formatDate } = params;

    const columns = [
        {
            accessorKey: 'tableUpdatedAt',
            header: lang.blotters.date,
            cell: ({ row, getValue }: CellContext<PortalDepositRow, any>) => {
                const value = getValue();
                return <div className="">{value}</div>;
            }
        },
        {
            accessorKey: 'tableAmount',
            header: lang.blotters.amount,
            cell: ({ row }: CellContext<PortalDepositRow, any>) => {
                const amount = row.original.amount;
                const ccy = row.original.currencyName;

                return generalAmountCell({
                    amount,
                    ccy,
                    formatQty
                });
            }
        },
        {
            accessorKey: 'tableMethod',
            header: lang.blotters.method,
            cell: ({ row, getValue }: CellContext<PortalDepositRow, any>) => getValue()
        },
        {
            accessorKey: 'tableStatus',
            header: lang.blotters.status,
            cell: ({ row, getValue }: CellContext<PortalDepositRow, string>) => {
                const value = getValue();

                return (
                    <div
                        className={cn('capitalize flex items-center gap-1', {
                            'text-yellow-400': value === 'Processing',
                            'text-brand-green-light': value === 'Completed',
                            'text-brand-red-light': ['Canceled', 'Rejected'].includes(value)
                        })}>
                        {value === 'Processing' ? <MdOutlineQueryBuilder className="h-4 w-4" /> : null}
                        {value === 'Completed' ? <MdOutlineCheckCircleOutline className="h-4 w-4" /> : null}
                        {value === 'Canceled' || value === 'Rejected' ? <MdErrorOutline className="h-4 w-4" /> : null}
                        {value}
                    </div>
                );
            }
        }
    ];
    return columns;
};

export const convertToTableRow = (
    request: DepositWithdrawalRequestHistory,
    formatQty,
    formatDate
): PortalDepositRow => {
    const amount = BigNumber(request.amount).toNumber();

    const ccy = request.currencyName;
    const { show, type } = getCurrency(ccy || '');

    const symbol = getSymbolFromCurrency(show || '') || '';

    const tableUpdatedAt = formatDate.format(new Date(request.updatedAt));
    const tableAmount = isNaN(amount) || amount === 0 ? '-' : `${symbol}${formatQty.format(amount)}`;

    let tableMethod = '';
    if (request.type?.includes('Internal-Transfer')) {
        tableMethod = 'Internal Transfer';
    } else if (type === 'Fiat') {
        tableMethod = 'Bank Transfer';
    } else if (type === 'Crypto') {
        tableMethod = 'Crypto Transfer';
    }

    let tableStatus;
    if (request.status === 'Processing') {
        tableStatus = 'Processing';
    } else if (request.status === 'Completed') {
        tableStatus = 'Completed';
    } else if (['Canceled', 'Rejected'].includes(request.status)) {
        tableStatus = capitalize(request.status);
    } else {
        tableStatus = capitalize(request.status);
    }

    return {
        ...request,
        tableUpdatedAt,
        tableAmount,
        tableMethod,
        tableStatus
    };
};
